import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, Panel, StatTile, EmptyState, Button, Chip, FormRow, Textarea, Select, Drawer, useToast } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post, patch } from '../lib/api'
import { ago, num } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { useSession, isAdmin } from '../lib/session'

type Status = 'researching' | 'referring' | 'contacted' | 'pitched' | 'won' | 'lost'
type Target = {
  id: string; name: string; status: Status; category_id: string; category_name: string
  phone: string | null; website: string | null; contact_name: string | null; pitch: string | null; notes: string | null; source: string | null
  areas: { id: string; name: string }[]; referrals: number; delivered: number; referrals_30d: number; last_referral_at: string | null; created_at: string
}
type Demand = { category_id: string; name: string; leads: number; leads_7d: number; unsold: number; clients: number; targets: number; top_areas: { name: string; n: number }[] }
type Referral = { id: string; created_at: string; post_id: string; summary: string | null; post_url: string | null; area_name: string | null; group_name: string | null; rep_name: string | null; delivered: boolean; responded_at: string | null }

export const STATUS: Record<Status, { label: string; tone?: 'pos' | 'warn' | 'neg' | 'accent'; hint: string }> = {
  researching: { label: 'Researching', hint: 'Not referred yet. Check their reviews first.' },
  referring: { label: 'Sending referrals', tone: 'accent', hint: 'Reps recommend them, free, when no client covers a lead.' },
  contacted: { label: 'Contacted', tone: 'accent', hint: 'Referrals continue while you talk.' },
  pitched: { label: 'Pitched', tone: 'warn', hint: 'Referrals continue while they decide.' },
  won: { label: 'Won', tone: 'pos', hint: 'A client now. No more free referrals.' },
  lost: { label: 'Not interested', tone: 'neg', hint: 'No more referrals.' },
}
const ORDER: Status[] = ['researching', 'referring', 'contacted', 'pitched', 'won', 'lost']

/**
 * Target businesses: companies Handpicked wants as clients. Reps send them
 * real referrals for free, so the pitch is "we have already sent you N
 * customers". Above them, where the demand is and who covers it.
 */
export default function Targets() {
  const { data, error, reload } = useApi<{ targets: Target[]; demand: Demand[] }>('/api/targets')
  const cats = useApi<{ categories: { id: string; name: string; active: boolean }[] }>('/api/categories')
  const areas = useApi<{ areas: { id: string; name: string; active: boolean }[] }>('/api/areas')
  const [adding, setAdding] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const live = data.targets.filter((t) => ['referring', 'contacted', 'pitched'].includes(t.status))
  const sent = data.targets.reduce((n, t) => n + t.referrals, 0)
  const delivered = data.targets.reduce((n, t) => n + t.delivered, 0)
  const gaps = data.demand.filter((d) => d.leads > 0 && d.clients === 0)
  return (
    <>
      <PageHeader title="Target businesses" subtitle="Companies we want as clients. Reps send them real referrals for free, so when you call you can say we have already sent them customers."
                  actions={<Button onClick={() => setAdding('')}>Add a target</Button>} />
      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile focal label="Trades with no client" value={num(gaps.length)} meta={`${num(gaps.reduce((n, d) => n + d.leads, 0))} leads in 30 days nobody is paid for`} />
        <StatTile label="Targets getting referrals" value={num(live.length)} meta={`${num(data.targets.length)} on the list`} />
        <StatTile label="Referrals sent" value={num(sent)} meta={`${num(delivered)} a rep has replied to`} />
        <StatTile label="Won" value={num(data.targets.filter((t) => t.status === 'won').length)} meta="became clients" />
      </div>

      <Panel>
        <div className="panel-header"><h2 className="panel-title">Where to recruit</h2><span className="muted">leads in the last 30 days</span></div>
        <p className="muted" style={{ marginTop: 0 }}>Trades with leads and no paying client are where a target pays off first. Add two or three good local companies for each, then turn their referrals on.</p>
        {data.demand.length === 0 ? <EmptyState title="No leads yet" body="Demand shows here once leads come in." /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Trade</th><th className="num">Leads</th><th className="num">This week</th><th>Where</th><th className="num">Clients</th><th className="num">Targets</th><th /></tr></thead>
              <tbody>
                {data.demand.map((d) => (
                  <tr key={d.category_id}>
                    <td><strong>{d.name}</strong></td>
                    <td className="num">{num(d.leads)}</td>
                    <td className="num">{num(d.leads_7d)}</td>
                    <td className="muted">{d.top_areas.map((a) => `${a.name} ${a.n}`).join(', ') || 'n/a'}</td>
                    <td className="num">{d.clients ? num(d.clients) : <span className="tone-warn">none</span>}</td>
                    <td className="num">{num(d.targets)}</td>
                    <td>{d.clients === 0 && <button type="button" className="link-button" onClick={() => setAdding(d.category_id)}>Add a target</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <div style={{ height: 'var(--s-6)' }} />
      <Panel>
        <div className="panel-header"><h2 className="panel-title">Targets</h2><span className="muted">{num(data.targets.length)}</span></div>
        {data.targets.length === 0 ? (
          <EmptyState title="No targets yet" body="Add a company you want as a client. It starts as Researching; turn its referrals on once you are happy for reps to recommend it in public." />
        ) : (
          <div className="stack-tight">
            {data.targets.map((t) => (
              <button key={t.id} type="button" className="target-row" onClick={() => setOpen(t.id)}>
                <span className="target-main">
                  <strong>{t.name}</strong>
                  <span className="muted">{t.category_name}{t.areas.length ? `, ${t.areas.map((a) => a.name).join(', ')}` : ', every area'}</span>
                </span>
                <span className="target-figs">
                  <span><strong className="num">{num(t.referrals)}</strong> sent</span>
                  <span><strong className="num">{num(t.delivered)}</strong> replied</span>
                  <span className="muted">{t.last_referral_at ? `last ${ago(t.last_referral_at)}` : 'none yet'}</span>
                </span>
                <Chip tone={STATUS[t.status].tone} dot>{STATUS[t.status].label}</Chip>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {adding !== null && (
        <TargetForm categories={(cats.data?.categories || []).filter((c) => c.active)} areas={(areas.data?.areas || []).filter((a) => a.active)}
                    initialCategory={adding} onClose={() => setAdding(null)} onSaved={() => { setAdding(null); reload() }} />
      )}
      {open && <TargetDrawer id={open} categories={(cats.data?.categories || []).filter((c) => c.active)} areas={(areas.data?.areas || []).filter((a) => a.active)}
                             onClose={() => setOpen(null)} onChanged={reload} />}
    </>
  )
}

type Form = { name: string; category_id: string; area_ids: string[]; phone: string; website: string; contact_name: string; pitch: string; notes: string; source: string }
const blank = (category = ''): Form => ({ name: '', category_id: category, area_ids: [], phone: '', website: '', contact_name: '', pitch: '', notes: '', source: '' })

function Fields({ f, set, categories, areas }: { f: Form; set: (f: Form) => void; categories: { id: string; name: string }[]; areas: { id: string; name: string }[] }) {
  const toggle = (id: string) => set({ ...f, area_ids: f.area_ids.includes(id) ? f.area_ids.filter((x) => x !== id) : [...f.area_ids, id] })
  return (
    <div className="stack-tight">
      <FormRow label="Business name" required><input className="field" value={f.name} onChange={(e) => set({ ...f, name: e.target.value })} maxLength={120} /></FormRow>
      <FormRow label="Trade" required>
        <Select value={f.category_id} onChange={(e) => set({ ...f, category_id: e.target.value })}>
          <option value="">Choose a trade</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </FormRow>
      <FormRow label="Areas they serve" help="None picked means every area.">
        <div className="filter-chips">
          {areas.map((a) => <button key={a.id} type="button" className="filter-chip" aria-pressed={f.area_ids.includes(a.id)} onClick={() => toggle(a.id)}>{a.name}</button>)}
        </div>
      </FormRow>
      <FormRow label="Phone"><input className="field" value={f.phone} onChange={(e) => set({ ...f, phone: e.target.value })} maxLength={40} /></FormRow>
      <FormRow label="Website"><input className="field" value={f.website} onChange={(e) => set({ ...f, website: e.target.value })} maxLength={200} /></FormRow>
      <FormRow label="Who to talk to"><input className="field" value={f.contact_name} onChange={(e) => set({ ...f, contact_name: e.target.value })} maxLength={120} /></FormRow>
      <FormRow label="Why we recommend them" help="Reps and the suggested reply use this. Facts only: reviews, specialty, years in business.">
        <Textarea value={f.pitch} onChange={(e) => set({ ...f, pitch: e.target.value })} rows={2} maxLength={600} />
      </FormRow>
      <FormRow label="Where you found them"><input className="field" value={f.source} onChange={(e) => set({ ...f, source: e.target.value })} maxLength={300} placeholder="Google, 4.8 stars, 210 reviews" /></FormRow>
      <FormRow label="Notes"><Textarea value={f.notes} onChange={(e) => set({ ...f, notes: e.target.value })} rows={3} maxLength={4000} /></FormRow>
    </div>
  )
}

function TargetForm({ categories, areas, initialCategory, onClose, onSaved }: { categories: { id: string; name: string }[]; areas: { id: string; name: string }[]; initialCategory: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [f, setF] = useState<Form>(blank(initialCategory))
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try { await post('/api/targets', f); toast(`${f.name} added. Turn referrals on when you are ready.`, 'pos'); onSaved() }
    catch (e) { toast((e as Error).message, 'neg'); setBusy(false) }
  }
  return (
    <Drawer title="Add a target business" onClose={onClose}
            footer={<div className="row-tight"><Button onClick={save} disabled={busy || f.name.trim().length < 2 || !f.category_id}>{busy ? 'Adding' : 'Add target'}</Button><Button variant="ghost" onClick={onClose}>Cancel</Button></div>}>
      <Fields f={f} set={setF} categories={categories} areas={areas} />
    </Drawer>
  )
}

function TargetDrawer({ id, categories, areas, onClose, onChanged }: { id: string; categories: { id: string; name: string }[]; areas: { id: string; name: string }[]; onClose: () => void; onChanged: () => void }) {
  const { data, error, reload } = useApi<{ target: Target; referrals: Referral[] }>(`/api/targets/${id}`)
  const { me } = useSession()
  const admin = isAdmin(me)
  const toast = useToast()
  const [edit, setEdit] = useState<Form | null>(null)
  const [busy, setBusy] = useState(false)
  const t = data?.target
  const save = async (body: Partial<Form> & { status?: Status }, msg: string) => {
    setBusy(true)
    try { await patch(`/api/targets/${id}`, body); toast(msg, 'pos'); setEdit(null); reload(); onChanged() }
    catch (e) { toast((e as Error).message, 'neg') }
    setBusy(false)
  }
  return (
    <Drawer title={t?.name || 'Target'} onClose={onClose}
            footer={edit ? <div className="row-tight"><Button onClick={() => save(edit, 'Saved')} disabled={busy}>Save</Button><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></div> : undefined}>
      {error && <ErrorBlock error={error} />}
      {!t ? <LoadingBlock /> : edit ? <Fields f={edit} set={setEdit} categories={categories} areas={areas} /> : (
        <div className="stack">
          <div className="stack-tight">
            <div className="row-tight" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip tone={STATUS[t.status].tone} dot>{STATUS[t.status].label}</Chip>
              <span className="muted">{STATUS[t.status].hint}</span>
            </div>
            <label className="row-tight" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <span>Move to</span>
              <Select aria-label="Status" value={t.status} disabled={busy}
                      onChange={(e) => save({ status: e.target.value as Status }, `${t.name}: ${STATUS[e.target.value as Status].label}`)}>
                {ORDER.map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
              </Select>
            </label>
          </div>

          <div className="target-pitch">
            <span className="s-label muted">When you call them</span>
            <p style={{ margin: 0 }}>
              {t.referrals
                ? `We have sent ${t.name} ${t.referrals} referral${t.referrals === 1 ? '' : 's'}${t.delivered ? `, ${t.delivered} of them recommended in a reply to the person asking` : ''}, for free.`
                : `No referrals yet. ${t.status === 'researching' ? 'Turn referrals on to start sending them leads.' : 'They go out when a lead in their trade and area has no paying client.'}`}
            </p>
          </div>

          <dl className="deflist">
            <dt>Trade</dt><dd>{t.category_name}</dd>
            <dt>Areas</dt><dd>{t.areas.length ? t.areas.map((a) => a.name).join(', ') : 'Every area'}</dd>
            {t.contact_name && <><dt>Contact</dt><dd>{t.contact_name}</dd></>}
            {t.phone && <><dt>Phone</dt><dd>{t.phone}</dd></>}
            {t.website && <><dt>Website</dt><dd>{t.website}</dd></>}
            {t.pitch && <><dt>Why we recommend them</dt><dd>{t.pitch}</dd></>}
            {t.source && <><dt>Found</dt><dd>{t.source}</dd></>}
            {t.notes && <><dt>Notes</dt><dd style={{ whiteSpace: 'pre-wrap' }}>{t.notes}</dd></>}
          </dl>
          <div><Button variant="secondary" onClick={() => setEdit({ name: t.name, category_id: t.category_id, area_ids: t.areas.map((a) => a.id), phone: t.phone || '', website: t.website || '', contact_name: t.contact_name || '', pitch: t.pitch || '', notes: t.notes || '', source: t.source || '' })}>Edit details</Button></div>

          <div>
            <h3 style={{ margin: '0 0 var(--s-3)' }}>Referrals ({data.referrals.length})</h3>
            {data.referrals.length === 0 ? <p className="muted">None yet.</p> : (
              <ul className="target-refs">
                {data.referrals.map((r) => (
                  <li key={r.id}>
                    {admin ? <Link to={`/leads/${r.post_id}`}>{r.summary || 'A request'}</Link>
                      : r.post_url ? <a href={r.post_url} target="_blank" rel="noopener noreferrer">{r.summary || 'A request'}</a> : <span>{r.summary || 'A request'}</span>}
                    <span className="muted">{[r.area_name, r.group_name, ago(r.created_at)].filter(Boolean).join(' · ')}</span>
                    <span className={r.delivered ? 'tone-pos' : 'muted'}>{r.delivered ? `Recommended by ${r.rep_name || 'a rep'}${r.responded_at ? ` ${ago(r.responded_at)}` : ''}` : `With ${r.rep_name || 'a rep'}, not replied yet`}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Drawer>
  )
}
